import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import { PageLoader, InlineLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useContractList, useDeleteContract } from '../../entities/contract';
import { readListJsSort, attachListJsSortPersistence } from '../../shared/useSortPreference';
import List from 'list.js';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import Select from '../../components/form/AppSelect';
import { Link } from 'react-router-dom';
import { Alert, Button, Form, InputGroup } from 'react-bootstrap';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import StatusBadge from '../../components/StatusBadge';
import { useCurrency } from '../../shared/CurrencyContext';
import { COLOR_SUCCESS, COLOR_WARNING, COLOR_PRIMARY, COLOR_DANGER, COLOR_SECONDARY } from '../../shared/colorUtils';
import '../../shared/ReactSelectDarkMode.css';
import { useConfirm } from '../../shared/ConfirmContext';
// Custom styles for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

function Contracts() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  // Project conversion modal state
  const [selectedProject, setSelectedProject] = useState('');
  const [customContractName, setCustomContractName] = useState('');
  const [showConversionModal, setShowConversionModal] = useState(false);

  const listInstance = useRef(null);
  const tableRef = useRef(null);

  const { data: contracts = [], isLoading } = useContractList();

  const { data: allProjects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get('projects/api/projects/'),
    select: (data) => data || [],
  });

  const projects = useMemo(() => allProjects.filter(p => !p.locked), [allProjects]);

  const convertMutation = useMutation({
    mutationFn: (data) => api.post('contracts/api/contracts/create_from_project/', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', 'list'] });
      setSelectedProject('');
      setCustomContractName('');
      setShowConversionModal(false);
      toast.success(t('contracts.conversion.success'));
    },
    onError: (err) => setError(err.message || t('contracts.conversion.error')),
  });

  const deleteMutation = useDeleteContract();

  // Initialize List.js when contracts data is loaded
  useEffect(() => {
    if (contracts.length > 0 && tableRef.current && !listInstance.current) {
      listInstance.current = new List('contracts-list', {
        valueNames: [
          'contract-name',
          'contractor-name',
          'customer-name',
          'location',
          'start-date',
          'end-date',
          'status-name',
          'budget'
        ],
        page: 50,
        pagination: true
      });

      // Restore saved sort state
      const saved = readListJsSort('contracts');
      if (saved?.col) {
        listInstance.current.sort(saved.col, { order: saved.order || 'asc' });
      }

      // Persist sort state on each click
      const cleanupSort = attachListJsSortPersistence('contracts', tableRef);

      return () => {
        cleanupSort();
        if (listInstance.current) {
          try {
            if (typeof listInstance.current.destroy === 'function') {
              listInstance.current.destroy();
            } else {
              listInstance.current = null;
            }
          } catch (error) {
            console.warn('Error cleaning up list.js instance:', error);
            listInstance.current = null;
          }
        }
      };
    }
  }, [contracts]);

  const handleProjectConversion = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      setError(t('contracts.conversion.noProjects'));
      return;
    }

    if (!await confirm(t('contracts.conversion.confirmConversion'))) {
      return;
    }

    convertMutation.mutate({
      project_id: selectedProject,
      name: customContractName || undefined,
    });
  };

  const handleShowConversionModal = () => {
    resetConversionForm();
    setShowConversionModal(true);
  };

  const handleCloseConversionModal = () => {
    setShowConversionModal(false);
    resetConversionForm();
  };

  const handleDelete = async (contractId) => {
    if (await confirm(t('contracts.confirmDelete'))) {
      deleteMutation.mutate(contractId, {
        onError: (err) => setError(err.message || t('contracts.deleteError')),
      });
    }
  };

  const resetConversionForm = () => {
    setSelectedProject('');
    setCustomContractName('');
    setError(null);
  };

  // Transform projects to react-select options
  const projectOptions = useMemo(() => projects.map(project => ({
    value: project.id,
    label: `${project.name} - ${project.customer_name || t('contracts.conversion.noCustomerFallback')}`
  })), [projects, t]);

  const handleProjectSelectChange = (selectedOption) => {
    setSelectedProject(selectedOption ? selectedOption.value : '');
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(i18n.language?.startsWith('cs') ? 'cs-CZ' : 'en-US');
  };

  const getStatusHex = (statusName) => {
    const status = statusName?.toLowerCase();
    switch (status) {
      case 'aktivní':
      case 'active':    return COLOR_SUCCESS;
      case 'plánováno':
      case 'planned':   return COLOR_WARNING;
      case 'dokončeno':
      case 'completed': return COLOR_PRIMARY;
      case 'zrušeno':
      case 'cancelled': return COLOR_DANGER;
      default:          return COLOR_SECONDARY;
    }
  };

  if (isLoading) {
    return (
      <PageShell
        pretitle={t('contracts.overview')}
        title={t('contracts.title')}
        description={t('contracts.subtitle')}
      >
        <PageSection>
          <PageLoader />
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('contracts.overview')}
      title={t('contracts.title')}
      description={t('contracts.subtitle')}
      actions={
        <>
          <Button as={Link} to="/contracts/statistics" variant="outline-primary">
            <i className="ti ti-chart-line me-2" />
            {t('contracts.statistics')}
          </Button>
          {canManage && (
            <Button type="button" onClick={handleShowConversionModal}>
              <i className="ti ti-file-plus me-2" />
              {t('contracts.convertFromProject')}
            </Button>
          )}
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="app-inline-alert">{error}</Alert>
      )}

      <PageSection
        title={t('contracts.title')}
        description={t('contracts.sectionDescription')}
        bodyClassName="p-0"
      >
        <div id="contracts-list">
          <div className="app-toolbar p-3 border-bottom">
            <InputGroup className="app-search">
              <InputGroup.Text>
                <i className="ti ti-search" aria-hidden="true" />
              </InputGroup.Text>
              <Form.Control
                type="search"
                className="search"
                placeholder={t('contracts.searchPlaceholder')}
              />
            </InputGroup>
          </div>
          {contracts.length === 0 ? (
            <EmptyState
              title={t('contracts.emptyTitle')}
              message={t('contracts.emptyMessage')}
              action={canManage ? (
                <Button type="button" onClick={handleShowConversionModal}>
                  <i className="ti ti-file-plus me-2" />
                  {t('contracts.convertFromProject')}
                </Button>
              ) : null}
            />
          ) : (
            <>
              <div className="table-responsive">
                <table className="table app-table mb-0" ref={tableRef}>
                    <thead>
                      <tr>
                        <th className="sort" data-sort="contract-name">{t('contracts.fields.name')}</th>
                        <th className="sort" data-sort="contractor-name">{t('contracts.fields.contractor')}</th>
                        <th className="sort" data-sort="customer-name">{t('contracts.fields.customer')}</th>
                        <th className="sort" data-sort="location">{t('contracts.fields.location')}</th>
                        <th className="sort" data-sort="start-date">{t('contracts.fields.startDate')}</th>
                        <th className="sort" data-sort="end-date">{t('contracts.fields.endDate')}</th>
                        <th className="sort" data-sort="status-name">{t('contracts.fields.status')}</th>
                        <th className="sort" data-sort="budget">{t('contracts.fields.budget')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="list">
                      {contracts.map((contract) => (
                        <tr key={contract.id}>
                          <td className="contract-name">
                            <Link to={`/contracts/contract/${contract.id}`} className="text-decoration-none">
                              {contract.name}
                              {contract.locked && (
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon ms-1 text-warning">
                                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                              )}
                            </Link>
                          </td>
                          <td className="contractor-name">{contract.contractor_name || '-'}</td>
                          <td className="customer-name">{contract.customer_name || '-'}</td>
                          <td className="location">{contract.location || '-'}</td>
                          <td className="start-date">{formatDate(contract.start_date)}</td>
                          <td className="end-date">{formatDate(contract.end_date)}</td>
                          <td className="status-name">
                            <StatusBadge color={getStatusHex(contract.status_name)} label={contract.status_name || '-'} />
                          </td>
                          <td className="budget">
                            {contract.currency_detail
                              ? formatCurrency(contract.budget, { currency: contract.currency_detail })
                              : formatCurrency(contract.budget)}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm" role="group">
                              <Button as={Link} to={`/contracts/contract/${contract.id}`} variant="outline-primary" size="sm">
                                <i className="ti ti-eye"></i>
                              </Button>
                              {canManage && (
                                  <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(contract.id)}
                                  disabled={contract.locked}
                                >
                                  <i className="ti ti-trash"></i>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="app-table-footer">
                  <ul className="pagination justify-content-center mb-0"></ul>
                </div>
            </>
          )}
        </div>
      </PageSection>

      {/* Project to Contract Conversion Modal */}
      <Modal
        id="conversionModal"
        title={t('contracts.conversion.title')}
        size="md"
        show={showConversionModal}
        onHide={handleCloseConversionModal}
      >
        <form onSubmit={handleProjectConversion}>
          <div className="modal-body">
            {error && (
              <Alert variant="danger">{error}</Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label htmlFor="selectedProject">{t('contracts.conversion.selectProject')} *</Form.Label>
              <Select
                id="selectedProject"
                options={projectOptions}
                value={projectOptions.find(option => option.value === selectedProject) || null}
                onChange={handleProjectSelectChange}
                placeholder={t('contracts.conversion.selectProject')}
                isClearable
                theme={selectTheme}
                classNamePrefix="react-select"
                className="react-select-container"
              />
              {projects.length === 0 && (
                <div className="form-hint text-muted">
                  {t('contracts.conversion.noProjects')}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label htmlFor="customContractName">{t('contracts.conversion.customName')}</Form.Label>
              <Form.Control
                type="text"
                id="customContractName"
                value={customContractName}
                onChange={(e) => setCustomContractName(e.target.value)}
                placeholder={t('contracts.conversion.customNamePlaceholder')}
              />
              <div className="form-hint">
                {t('contracts.conversion.customNamePlaceholder')}
              </div>
            </Form.Group>

            <Alert variant="warning">
              <div className="d-flex">
                <i className="ti ti-alert-triangle alert-icon me-3 fs-4"></i>
                <div>
                  {t('contracts.conversion.confirmConversion')}
                </div>
              </div>
            </Alert>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="secondary" onClick={handleCloseConversionModal}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={convertMutation.isPending || !selectedProject}>
              {convertMutation.isPending ? (
                <>
                  <InlineLoader className="me-2" />
                  {t('contracts.conversion.converting')}
                </>
              ) : (
                t('contracts.conversion.convert')
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </PageShell>
  );
}

export default Contracts;
